import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const DEMO_APP_PATH = '/salar-resilience-terrain-app/index.html';

const ResilienceTerrain = () => {
  return (
    <div className="min-h-screen" style={{ background: '#0A1628' }} data-testid="resilience-terrain-page">
      <Helmet>
        <title>SALAR Resilience Terrain Explorer | SALAR Technologies LLC</title>
        <meta name="description" content="Interactive public-safe semiconductor resilience terrain demo from SALAR Technologies LLC." />
        <meta property="og:title" content="SALAR Resilience Terrain Explorer" />
        <meta property="og:description" content="Interactive public-safe semiconductor resilience terrain demo from SALAR Technologies LLC." />
        <meta property="og:url" content="https://www.salartechnologies.com/salar-resilience-terrain" />
        <link rel="canonical" href="https://www.salartechnologies.com/salar-resilience-terrain" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <style>{`
        .terrain-frame-card{max-width:1600px;margin:0 auto;border-radius:.75rem;overflow:hidden;border:1px solid rgba(34,211,238,.30);background:#010816}
        .terrain-frame-shell{width:100%;overflow-x:auto;overflow-y:hidden;-webkit-overflow-scrolling:touch;background:#010816;scrollbar-color:rgba(34,211,238,.65) rgba(1,8,22,.85);scrollbar-width:thin}
        .terrain-frame-shell::-webkit-scrollbar{height:10px}.terrain-frame-shell::-webkit-scrollbar-track{background:#010816}.terrain-frame-shell::-webkit-scrollbar-thumb{background:rgba(34,211,238,.55);border-radius:999px}
        .terrain-frame{display:block;width:1440px;min-width:1440px;height:1200px;min-height:1200px;border:0;background:#010816}
        @media(min-width:1500px){.terrain-frame{width:100%;min-width:1366px}}
        @media(max-width:900px){.terrain-frame{width:1280px;min-width:1280px;height:1050px;min-height:1050px}.terrain-frame-card{border-radius:.5rem;margin-left:-.5rem;margin-right:-.5rem}}
      `}</style>

      <section
        className="relative text-white py-16 lg:py-20"
        style={{
          backgroundImage:
            "linear-gradient(rgba(10,22,40,0.72), rgba(10,22,40,0.92)), url('/img/salar_mission_continuity.webp')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="resilience-terrain-hero"
      >
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto text-center">
            <Badge variant="outline" className="border-cyan-400 text-cyan-200 mb-5 tracking-widest text-xs" data-testid="terrain-status-badge">
              LOCAL INTERACTIVE DEMO
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-5 leading-tight">SALAR Resilience Terrain Explorer</h1>
            <p className="text-lg md:text-xl font-semibold text-cyan-200 mb-4" data-testid="resilience-terrain-tagline">
              Mission electronics fail across hidden terrain.
            </p>
            <p className="text-base md:text-lg text-blue-100 leading-relaxed max-w-3xl mx-auto mb-6">
              Explore the original command-center demo directly on salartechnologies.com.
            </p>
          </div>
        </div>
      </section>

      <section className="py-8" style={{ background: '#06111F' }} data-testid="terrain-original-app-wrapper">
        <div className="container mx-auto px-3 sm:px-6">
          <div className="mb-4 rounded-lg border border-cyan-400/35 bg-cyan-950/30 p-4 text-cyan-50" data-testid="terrain-device-notice">
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-cyan-300 mb-2">Tablet / Mobile View</div>
            <p className="text-sm leading-relaxed mb-3">This dashboard is desktop-width. On iPad or phone, use the full-screen button or swipe sideways inside the frame.</p>
            <a href={DEMO_APP_PATH}>
              <Button className="w-full sm:w-auto bg-cyan-600 hover:bg-cyan-700 text-white font-semibold">Open Full-Screen Demo</Button>
            </a>
          </div>

          <div className="terrain-frame-card">
            <div className="terrain-frame-shell" data-testid="terrain-frame-scroll-shell">
              <iframe src={DEMO_APP_PATH} title="SALAR Resilience Terrain Explorer" allow="clipboard-write" className="terrain-frame" data-testid="terrain-original-app-iframe" />
            </div>
          </div>
          <p className="mt-4 text-center text-sm italic text-cyan-100/80">Illustrative public-safe visualization.</p>
        </div>
      </section>

      <section className="py-14" style={{ background: '#0A1628' }} data-testid="terrain-use-rules">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="rounded-lg p-6 border border-cyan-500/25" style={{ background: '#1A2744' }}>
              <div className="text-cyan-300 text-xs font-bold tracking-widest uppercase mb-3">What to verify</div>
              <h2 className="text-2xl font-bold text-white mb-4">Original Command-Center Demo</h2>
              <ul className="space-y-2 text-sm text-blue-100 leading-relaxed list-disc list-inside">
                <li>Left Systemic Parameters panel</li>
                <li>Select Active Asset dropdown</li>
                <li>Analytical Scenario dropdown</li>
                <li>8-step workflow trace</li>
                <li>Scenario Terrain and Decision Log tabs</li>
                <li>Tactical Incident Deck and Cockpit Export Center</li>
              </ul>
            </div>
            <div className="rounded-lg p-6 border border-amber-400/40" style={{ background: 'linear-gradient(180deg, rgba(245,158,11,0.08), rgba(10,22,40,0.4))' }}>
              <div className="text-amber-300 text-xs font-bold tracking-widest uppercase mb-3">Use rules</div>
              <h2 className="text-2xl font-bold text-white mb-4">Public-Safe Inputs Only</h2>
              <p className="text-sm text-blue-100 leading-relaxed">Use synthetic examples, public descriptions, or sanitized technical scenarios only.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 text-white" style={{ background: '#0F1A2B' }}>
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Request a SALAR Capability Briefing</h2>
            <p className="text-base text-blue-100 leading-relaxed mb-8">Walk through the Resilience Terrain with engineering-led context. Founder-led review, 30 to 45 minutes.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href="mailto:vikrant@salartechnologies.com?subject=SALAR%20Capability%20Briefing%20Request" data-testid="terrain-briefing-mailto-btn">
                <Button size="lg" className="bg-cyan-600 hover:bg-cyan-700 text-white font-semibold">Request a Capability Briefing</Button>
              </a>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-cyan-300 text-cyan-200 hover:bg-cyan-300 hover:text-[#0a1628]" data-testid="terrain-contact-btn">Other Ways to Contact<ArrowRight className="ml-2" size={16} /></Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ResilienceTerrain;
